import { useEffect, useState } from "react";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
// import { useTheme } from "./ThemeContext"; // Removed unused import
import SpotlightSearch from "./SpotlightSearch";
import SearchBar from "./SearchBar";
import styles from "./Home.module.css";

const db = getFirestore();

export default function Home({ user, onNavigate, isAdmin = false }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalIngredients: 0,
    totalStores: 0,
    lowStockItems: 0,
    outOfStockItems: 0,
    recentProduction: 0
  });
  const [loading, setLoading] = useState(true);
  const [showSpotlight, setShowSpotlight] = useState(false);
  // const { isDarkMode } = useTheme(); // Removed unused variable

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        console.log("Starting to fetch stats...");
        console.log("User:", user);
        console.log("User UID:", user?.uid);
        console.log("Database instance:", db);
        
        // Test authentication state
        if (!user) {
          console.error("No user authenticated");
          return;
        }
        
        // Test basic connectivity first
        console.log("Testing collection access...");
        
        // Try to fetch just one collection first to test connectivity
        console.log("Testing products collection...");
        const testProductsSnap = await getDocs(collection(db, "products"));
        console.log("Products test successful, count:", testProductsSnap.size);
        
        // Fetch all collections in parallel
        const [productsSnap, ingredientsSnap, storesSnap, productionSnap] = await Promise.all([
          getDocs(collection(db, "products")).catch(err => {
            console.error("Error fetching products:", err);
            return { docs: [], size: 0 };
          }),
          getDocs(collection(db, "ingredients")).catch(err => {
            console.error("Error fetching ingredients:", err);
            return { docs: [], size: 0 };
          }),
          getDocs(collection(db, "stores")).catch(err => {
            console.error("Error fetching stores:", err);
            return { docs: [], size: 0 };
          }),
          getDocs(collection(db, "productionEvents")).catch(err => {
            console.error("Error fetching productionEvents:", err);
            return { docs: [], size: 0 };
          })
        ]);

        console.log("Fetched data:", {
          products: productsSnap.size,
          ingredients: ingredientsSnap.size,
          stores: storesSnap.size,
          productionEvents: productionSnap.size
        });

        // Calculate ingredient stock stats
        const ingredients = ingredientsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const lowStock = ingredients.filter(ing => {
          const currentStock = ing.currentStock || 0;
          const minStock = ing.minStock || 5;
          return currentStock <= minStock && currentStock > 0;
        }).length;
        
        const outOfStock = ingredients.filter(ing => 
          (ing.currentStock || 0) === 0
        ).length;

        // Calculate recent production (last 7 days)
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        const recentProduction = productionSnap.docs.filter(doc => {
          const data = doc.data();
          if (data.date && data.date.toDate) {
            return data.date.toDate() >= oneWeekAgo;
          }
          return false;
        }).length;

        const newStats = {
          totalProducts: productsSnap.size,
          totalIngredients: ingredientsSnap.size,
          totalStores: storesSnap.size,
          lowStockItems: lowStock,
          outOfStockItems: outOfStock,
          recentProduction: recentProduction
        };

        console.log("Calculated stats:", newStats);
        setStats(newStats);
      } catch (err) {
        console.error("Error fetching stats:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user]); // Added 'user' to dependency array


  // Extract first name from user
  let firstName = "";
  if (user?.displayName) {
    firstName = user.displayName.split(" ")[0];
  } else if (user?.email) {
    firstName = user.email.split("@")[0];
  }

  if (loading) {
    return (
      <div className={styles.homeContainer}>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.homeContainer}>
      {/* Welcome Header */}
      <div className={styles.welcomeSection}>
        <div className={styles.welcomeContent}>
          <h1 className={styles.welcomeTitle}>
            Welcome back, {firstName}! 👋
          </h1>
          <p className={styles.welcomeSubtitle}>
            Here's an overview of your production management system
          </p>
        </div>
      </div>

      {/* Search Bar */}
      <div className={styles.searchSection}>
        <SearchBar placeholder="Search products, ingredients, recipes, stores, items, vendors..." />
      </div>

      {/* Quick Stats Grid */}
      <div className={styles.statsGrid}>
        <div className={`${styles.statCard} ${styles.productsCard}`}>
          <div className={styles.statIcon}>📦</div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{stats.totalProducts}</div>
            <div className={styles.statLabel}>Total Products</div>
          </div>
        </div>

        <div className={`${styles.statCard} ${styles.ingredientsCard}`}>
          <div className={styles.statIcon}>🥕</div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{stats.totalIngredients}</div>
            <div className={styles.statLabel}>Ingredients</div>
          </div>
        </div>

        <div className={`${styles.statCard} ${styles.storesCard}`}>
          <div className={styles.statIcon}>🏬</div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{stats.totalStores}</div>
            <div className={styles.statLabel}>Stores</div>
          </div>
        </div>

        <div className={`${styles.statCard} ${styles.productionCard}`}>
          <div className={styles.statIcon}>📈</div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{stats.recentProduction}</div>
            <div className={styles.statLabel}>Recent Production</div>
            <div className={styles.statNote}>Last 7 days</div>
          </div>
        </div>
      </div>

      {/* Alerts Section */}
      {(stats.lowStockItems > 0 || stats.outOfStockItems > 0) && (
        <div className={styles.alertsSection}>
          <h2 className={styles.alertsTitle}>⚠️ Inventory Alerts</h2>
          <div className={styles.alertsGrid}>
            {stats.outOfStockItems > 0 && (
              <div className={`${styles.alertCard} ${styles.criticalAlert}`}>
                <div className={styles.alertIcon}>🚨</div>
                <div className={styles.alertContent}>
                  <div className={styles.alertNumber}>{stats.outOfStockItems}</div>
                  <div className={styles.alertLabel}>Out of Stock</div>
                  <div className={styles.alertAction}>Needs immediate attention</div>
                </div>
              </div>
            )}
            
            {stats.lowStockItems > 0 && (
              <div className={`${styles.alertCard} ${styles.warningAlert}`}>
                <div className={styles.alertIcon}>⚠️</div>
                <div className={styles.alertContent}>
                  <div className={styles.alertNumber}>{stats.lowStockItems}</div>
                  <div className={styles.alertLabel}>Low Stock</div>
                  <div className={styles.alertAction}>Consider restocking</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className={styles.quickActionsSection}>
        <h2 className={styles.sectionTitle}>Quick Actions</h2>
        <div className={styles.quickActionsGrid}>
          {isAdmin && (
            <div
              className={styles.quickActionCard}
              onClick={() => navigate("/admin")}
            >
              <div className={styles.actionIcon}>🛡️</div>
              <h3 className={styles.actionTitle}>Admin Dashboard</h3>
              <p className={styles.actionDescription}>Approve users and run admin tools</p>
            </div>
          )}
          <div 
            className={styles.quickActionCard}
            onClick={() => onNavigate && onNavigate("products")}
          >
            <div className={styles.actionIcon}>➕</div>
            <h3 className={styles.actionTitle}>Add Product</h3>
            <p className={styles.actionDescription}>Create a new product with recipes</p>
          </div>
          
          <div 
            className={styles.quickActionCard}
            onClick={() => onNavigate && onNavigate("inventory")}
          >
            <div className={styles.actionIcon}>📊</div>
            <h3 className={styles.actionTitle}>Check Inventory</h3>
            <p className={styles.actionDescription}>Review stock levels and restock items</p>
          </div>
          
          <div 
            className={styles.quickActionCard}
            onClick={() => onNavigate && onNavigate("analytics")}
          >
            <div className={styles.actionIcon}>📈</div>
            <h3 className={styles.actionTitle}>View Analytics</h3>
            <p className={styles.actionDescription}>Analyze production trends and metrics</p>
          </div>
          
          <div 
            className={styles.quickActionCard}
            onClick={() => onNavigate && onNavigate("inventory")}
          >
            <div className={styles.actionIcon}>🛒</div>
            <h3 className={styles.actionTitle}>Shopping List</h3>
            <p className={styles.actionDescription}>Generate PDF shopping list for restocking</p>
          </div>
        </div>
      </div>

      {/* Recent Activity Preview */}
      <div className={styles.recentActivitySection}>
        <h2 className={styles.sectionTitle}>System Overview</h2>
        <div className={styles.overviewGrid}>
          <div className={styles.overviewCard}>
            <h3 className={styles.overviewTitle}>Inventory Status</h3>
            <div className={styles.overviewStats}>
              <div className={styles.overviewStat}>
                <span className={styles.overviewNumber} style={{ color: '#10b981' }}>
                  {stats.totalIngredients - stats.lowStockItems - stats.outOfStockItems}
                </span>
                <span className={styles.overviewLabel}>In Stock</span>
              </div>
              <div className={styles.overviewStat}>
                <span className={styles.overviewNumber} style={{ color: '#f59e0b' }}>
                  {stats.lowStockItems}
                </span>
                <span className={styles.overviewLabel}>Low Stock</span>
              </div>
              <div className={styles.overviewStat}>
                <span className={styles.overviewNumber} style={{ color: '#ef4444' }}>
                  {stats.outOfStockItems}
                </span>
                <span className={styles.overviewLabel}>Out of Stock</span>
              </div>
            </div>
          </div>
          
          <div className={styles.overviewCard}>
            <h3 className={styles.overviewTitle}>Production Activity</h3>
            <div className={styles.productionOverview}>
              <div className={styles.productionStat}>
                <span className={styles.productionNumber}>{stats.recentProduction}</span>
                <span className={styles.productionLabel}>Productions this week</span>
              </div>
              <div className={styles.productionNote}>
                {stats.recentProduction > 0 
                  ? "Production is active" 
                  : "No recent production activity"
                }
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Spotlight Search Modal */}
      <SpotlightSearch 
        isOpen={showSpotlight} 
        onClose={() => setShowSpotlight(false)} 
      />
    </div>
  );
}
